import { z } from 'zod';
import { AdminRole } from './auth.js';

export const InviteAdminInput = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(200),
  role: AdminRole,
});
export type InviteAdminInput = z.infer<typeof InviteAdminInput>;

export const AcceptInviteInput = z.object({
  token: z.string().min(1),
  password: z.string().min(8),
});
export type AcceptInviteInput = z.infer<typeof AcceptInviteInput>;

export const InviteDetails = z.object({
  email: z.string().email(),
  name: z.string(),
  role: AdminRole,
});
export type InviteDetails = z.infer<typeof InviteDetails>;

export const InviteCreatedResponse = z.object({
  id: z.string().uuid(),
  expires_at: z.string(),
  // Whether the invite email actually left (T-181). The invite row is created
  // either way — it's what the Resend recovery path operates on. 'config_error'
  // means every send will fail until MAIL_FROM/RESEND_API_KEY is fixed;
  // 'transient_error' means a retry (Resend button) is worth trying. Defaults
  // to 'sent' so an older payload without the field reads as the happy path.
  email_outcome: z.enum(['sent', 'config_error', 'transient_error']).default('sent'),
});
export type InviteCreatedResponse = z.infer<typeof InviteCreatedResponse>;

export const AdminListItem = z.object({
  id: z.string().uuid(),
  // Tolerant on read (z.string(), not .email()): a single malformed legacy row
  // must not 400 the whole list so it can still be removed in-app (T-093). The
  // write paths validate the format (InviteAdminInput + bootstrap-admin).
  email: z.string(),
  name: z.string(),
  role: AdminRole,
  // Owner capability (T-168); fail-closed default so an older payload reads as
  // non-owner.
  is_owner: z.boolean().default(false),
  created_at: z.string(),
});
export type AdminListItem = z.infer<typeof AdminListItem>;

export const AdminList = z.array(AdminListItem);

export const UpdateAdminRoleInput = z.object({ role: AdminRole });
export type UpdateAdminRoleInput = z.infer<typeof UpdateAdminRoleInput>;

// Grant/revoke the owner capability (T-168) — owner-only endpoint.
export const UpdateAdminOwnerInput = z.object({ is_owner: z.boolean() });
export type UpdateAdminOwnerInput = z.infer<typeof UpdateAdminOwnerInput>;

// Pending (unaccepted, unexpired) admin invites — for revoke/resend (T-095).
export const PendingInvite = z.object({
  id: z.string().uuid(),
  email: z.string(),
  name: z.string(),
  role: AdminRole,
  created_at: z.string(),
  expires_at: z.string(),
});
export type PendingInvite = z.infer<typeof PendingInvite>;

export const PendingInviteList = z.array(PendingInvite);
